import RewardCelebration from "@/components/RewardCelebration";
import ChapterCelebration from "@/components/ChapterCelebration";
import OptionPic from "@/components/OptionPic";
import { playCorrectDing, playLevelCheer, playEncourage } from "@/lib/sfx";
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Loader2, Star, Check, X, RotateCcw, Home, Trophy, Shield, Volume2, VolumeX, Sparkles, Heart, CheckCircle2, Crown, ArrowRight, PencilRuler, BookOpenText, Gift, Target, Turtle, Lightbulb } from "lucide-react";
import api, { formatError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { playVoice, stopVoice, readAlongSequence, getVoiceSpeed, setVoiceSpeed, answerSpeech } from "@/lib/tts";
import { BILINGUAL_SUBJECTS, isYoung, optionLabel, levelLabel } from "@/lib/subjects";
import CalculationPad from "@/components/Calculator";
import GeometryDiagram, { parseGeometry } from "@/components/GeometryDiagram";
import StoryDiagram, { parseStory } from "@/components/StoryDiagram";
import QuestionText from "@/components/QuestionText";
import KreolHint from "@/components/KreolHint";
import KikoCheer from "@/components/KikoCheer";
import KikoPath from "@/components/KikoPath";
import StarCatch from "@/components/StarCatch";
import StartGate from "@/components/StartGate";
import { MathText } from "@/components/MathText";
import { buildWorking } from "@/lib/arithmetic";
import { toast } from "sonner";
import QuestionImage from "@/components/QuestionImage";
import ReportQuestion from "@/components/ReportQuestion";

const CORRECT_MSGS = {
  en: ["Well done!", "Awesome!", "Fantastic!", "Brilliant!", "You got it!"],
  fr: ["Bravo !", "Super !", "Excellent !", "Bien joué !", "C'est ça !"],
};
const WRONG_MSGS = {
  en: ["Persevere, you'll get there!", "Keep trying, you can do it!", "Don't give up!", "Almost — remember this one!"],
  fr: ["Persévère, tu vas y arriver !", "Continue, tu peux le faire !", "N'abandonne pas !", "Presque — retiens bien ça !"],
};
const kikoWin = (msg, streak, fr) => (streak >= 10 ? (fr ? "10 de suite ! Champion !" : "10 in a row! Champion!")
  : streak === 5 ? (fr ? "5 de suite ! Wow !" : "5 in a row! Wow!") : streak === 3 ? (fr ? "3 de suite !" : "3 in a row!") : msg);
const kikoTry = (fr) => (fr ? "Essaie encore, tu peux le faire !" : "Keep trying, you can do it!");
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const SPEED_LABEL = {
  en: { fast: "Voice speed: fast", normal: "Voice speed: normal", slow: "Voice speed: slow", very_slow: "Voice speed: very slow" },
  fr: { fast: "Vitesse : rapide", normal: "Vitesse : normale", slow: "Vitesse : lente", very_slow: "Vitesse : très lente" },
};
const SPEED_SHORT = { en: { fast: "Fast", normal: "1×", slow: "Slow", very_slow: "Very slow" }, fr: { fast: "Rapide", normal: "1×", slow: "Lent", very_slow: "Très lent" } };

export default function QuizPlayer() {
  const { childId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const subject = params.get("subject");
  const isMaths = subject === "Maths";
  const bilingual = BILINGUAL_SUBJECTS.includes(subject);
  const chapter = params.get("chapter");
  const level = parseInt(params.get("level") || "1", 10);
  const levelName = params.get("levelName") || "";
  const progKey = `aa_quiz_${childId}_${subject}_${chapter}_${level}`;

  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [reportedQs, setReportedQs] = useState([]);
  const answersRef = useRef([]);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const advanceTimer = useRef(null);
  const [padOpen, setPadOpen] = useState(false);
  const copybookCorrectRef = useRef(0);
  const padUsedThisQRef = useRef(false);
  const [extraPads, setExtraPads] = useState([]);
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("aa_voice_on") !== "off");
  const [voiceLang, setVoiceLang] = useState(
    () => (bilingual ? localStorage.getItem("aa_voice_lang") || "en" : subject === "French" ? "fr" : "en")
  );
  const [readProgress, setReadProgress] = useState(null);
  const title = (bilingual && voiceLang === "fr" && params.get("titleFr")) || params.get("title");
  const [readingOpt, setReadingOpt] = useState(null);
  const [voiceSpeed, setSpeed] = useState(getVoiceSpeed);
  const [young, setYoung] = useState(false);
  const [started, setStarted] = useState(false);
  const [kiko, setKiko] = useState(null);
  const [star, setStar] = useState(0);
  const streakRef = useRef(0);

  const readAloud = (qq, lang = voiceLang) => {
    if (!qq) return;
    setReadProgress(0);
    setReadingOpt(null);
    readAlongSequence(qq.question, qq.options.map((o) => optionLabel(o, qq.has_image, lang)), lang, {
      onQuestionProgress: (f) => setReadProgress(f),
      onOption: (i) => setReadingOpt(i),
      onDone: () => { setReadProgress(null); setReadingOpt(null); },
    });
  };
  const readAlong = () => readAloud(questions[index]);

  const cycleSpeed = () => {
    const order = ["fast", "normal", "slow", "very_slow"];
    const next = order[(order.indexOf(voiceSpeed) + 1) % order.length];
    setVoiceSpeed(next);
    setSpeed(next);
    toast(SPEED_LABEL[voiceLang][next], { duration: 1500 });
    if (voiceOn && questions[index] && !revealed) readAloud(questions[index]);
  };

  useEffect(() => { setReadProgress(null); setReadingOpt(null); }, [index]);
  useEffect(() => () => stopVoice(), []);

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem("aa_voice_on", next ? "on" : "off");
    if (!next) stopVoice();
    else if (questions[index]) readAloud(questions[index]);
  };

  const levelLangRef = useRef("en");
  const checkingRef = useRef(false);

  const loadLevel = (lang) => {
    stopVoice();
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    copybookCorrectRef.current = 0;
    padUsedThisQRef.current = false;
    setLoading(true);
    setResult(null);
    setIndex(0);
    setSelected(null);
    setRevealed(false);
    setFeedback(null);
    levelLangRef.current = lang;
    api.post(`/children/${childId}/level/start`, { subject, chapter_id: chapter, level, lang })
      .then(({ data }) => {
        setYoung(isYoung(data.grade));
        setQuestions(data.questions);
        let restored = false;
        try {
          const saved = JSON.parse(localStorage.getItem(progKey) || "null");
          if (saved && saved.lang === lang && Array.isArray(saved.answers) && saved.answers.length === data.questions.length && !saved.done) {
            answersRef.current = saved.answers;
            setAnswers(saved.answers);
            setIndex(Math.min(saved.index || 0, data.questions.length - 1));
            restored = true;
            toast.success(lang === "fr" ? "On reprend où tu t'étais arrêté !" : "Resuming where you left off!");
          }
        } catch (e) { /* ignore */ }
        if (!restored) {
          answersRef.current = new Array(data.questions.length).fill(-1);
          setAnswers(new Array(data.questions.length).fill(-1));
        }
      })
      .catch((err) => {
        toast.error(formatError(err.response?.data?.detail) || "Could not load this level.");
        navigate(`/kid/${childId}/subject/${encodeURIComponent(subject)}`);
      })
      .finally(() => setLoading(false));
  };

  const switchLang = (lang) => {
    if (lang === voiceLang) return;
    setVoiceLang(lang);
    localStorage.setItem("aa_voice_lang", lang);
    loadLevel(lang);
  };

  // Auto read the current question aloud when it appears
  useEffect(() => {
    if (!loading && voiceOn && questions[index] && !result && (!young || started)) {
      readAloud(questions[index]);
    }
    return () => stopVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, loading, result]);

  useEffect(() => () => { stopVoice(); if (advanceTimer.current) clearTimeout(advanceTimer.current); }, []);

  // Clear any extra scratch calculators when moving to a new question
  useEffect(() => { setExtraPads([]); padUsedThisQRef.current = padOpen; }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remember if the child opened their copybook for this question
  useEffect(() => { if (padOpen) padUsedThisQRef.current = true; }, [padOpen]);

  // Persist quiz progress so the child can pause and resume the level
  useEffect(() => {
    if (loading || result) return;
    try { localStorage.setItem(progKey, JSON.stringify({ index, answers: answersRef.current, lang: levelLangRef.current, ts: Date.now() })); } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, answers, loading, result]);

  // Make sure the celebration/result is seen from the top
  useEffect(() => {
    if (result) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [result]);

  const didMountRef = useRef(false);
  useEffect(() => {
    if (didMountRef.current) return; // guard against StrictMode double-invoke
    didMountRef.current = true;
    loadLevel(voiceLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = async (optIdx) => {
    if (revealed || checkingRef.current) return;
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    stopVoice();
    checkingRef.current = true;
    setSelected(optIdx);
    const at = index;
    let res;
    try {
      ({ data: res } = await api.post(`/children/${childId}/answer-check`, {
        subject, chapter_id: chapter, level: Number(level), lang: levelLangRef.current, qi: at, chosen: optIdx }));
    } catch {
      checkingRef.current = false;
      setSelected(null);
      toast.error(voiceLang === "fr" ? "Connexion perdue, réessaie." : "Connection problem, please try again.");
      return;
    }
    checkingRef.current = false;
    setQuestions((qs) => qs.map((x, j) => (j === at ? { ...x, answer: res.answer, explanation: res.explanation } : x)));
    const next = [...answersRef.current];
    next[at] = optIdx;
    answersRef.current = next;
    setAnswers(next);
    setRevealed(true);
    const isCorrect = res.correct;
    const usedCopybook = isCorrect && padUsedThisQRef.current;
    if (usedCopybook) copybookCorrectRef.current += 1;
    const message = isCorrect ? pick(CORRECT_MSGS[voiceLang]) : pick(WRONG_MSGS[voiceLang]);
    setFeedback({ correct: isCorrect, usedCopybook, message });
    streakRef.current = isCorrect ? streakRef.current + 1 : 0;
    const fr = voiceLang === "fr";
    if (isCorrect) {
      playCorrectDing();
      const cbPraise = fr ? "Bravo ! Tu as utilisé ton cahier et trouvé la bonne réponse !" : "Well done! You used your copybook and found the right answer!";
      const said = usedCopybook ? cbPraise : young ? kikoWin(message, streakRef.current, fr) : streakRef.current % 10 === 0 ? kikoWin(message, 10, fr) : message;
      if (young || usedCopybook) setKiko({ n: Date.now(), msg: said, streak: usedCopybook ? Math.max(3, streakRef.current) : streakRef.current, kind: "win" });
      else if (streakRef.current % 10 === 0) setKiko({ n: Date.now(), msg: kikoWin(message, 10, fr), streak: 10, kind: "win" });
      if (voiceOn) setTimeout(() => playVoice(said, voiceLang), 450);
    } else {
      setKiko({ n: Date.now(), msg: kikoTry(fr), kind: "try" });
      if (voiceOn) setTimeout(() => playVoice(kikoTry(fr), voiceLang), 450);
    }
    if (young && (index + 1) % 5 === 0 && index + 1 < questions.length) setTimeout(() => setStar(Date.now()), 3000);
    // Never auto-advance: the child reads the explanation, then taps to continue.
  };

  const skipReported = () => {
    const next = [...answersRef.current];
    next[index] = -2;
    answersRef.current = next;
    setAnswers(next);
    goNext();
  };

  const advanceNow = () => {
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    goNext();
  };

  const goNext = async () => {
    stopVoice();
    if (advanceTimer.current) clearTimeout(advanceTimer.current);
    if (index < questions.length - 1) {
      setIndex(index + 1);
      setSelected(null);
      setRevealed(false);
      setFeedback(null);
    } else {
      await submit();
    }
  };

  const submit = async () => {
    setSubmitting(true);
    const payload = { subject, chapter_id: chapter, level, answers: answersRef.current, lang: voiceLang, copybook_correct: copybookCorrectRef.current };
    const post = () => api.post(`/children/${childId}/level/submit`, payload);
    try {
      let data;
      try {
        ({ data } = await post());
      } catch (firstErr) {
        // transient failure (backend busy / network blip) — retry once
        await new Promise((r) => setTimeout(r, 1200));
        ({ data } = await post());
      }
      setResult(data);
      // level finished — clear saved progress and copybook snapshots
      try {
        localStorage.removeItem(progKey);
        Object.keys(localStorage).filter((k) => k.startsWith(`${progKey}_q`)).forEach((k) => localStorage.removeItem(k));
      } catch (e) {}
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || (voiceLang === "fr" ? "Un souci est survenu. Ta progression est gardée — appuie encore pour réessayer." : "Something went wrong. Your progress is kept — tap again to retry."));
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    try {
      localStorage.removeItem(progKey);
      Object.keys(localStorage).filter((k) => k.startsWith(`${progKey}_q`)).forEach((k) => localStorage.removeItem(k));
    } catch (e) {}
    setResult(null);
    setIndex(0);
    setSelected(null);
    setRevealed(false);
    setFeedback(null);
    answersRef.current = new Array(questions.length).fill(-1);
    setAnswers(new Array(questions.length).fill(-1));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#EAF7F4] font-kids gap-4">
        <Loader2 className="animate-spin text-blue-600" size={40} />
        <p className="text-slate-500 font-parent">Preparing your questions…</p>
      </div>
    );
  }

  if (result) {
    return <ResultScreen result={result} subject={subject} title={title} voiceLang={voiceLang} onRetry={retry}
      starChildId={young ? null : childId}
      showUnlock={false}
      onUpgrade={() => navigate("/pricing")}
      onSubject={() => navigate(`/kid/${childId}/subject/${encodeURIComponent(subject)}`)}
      onCollection={() => navigate(`/kid/${childId}/collection`)}
      onHome={() => navigate(`/kid/${childId}`)} />;
  }

  const q = questions[index];
  const working = q ? buildWorking(q.question, voiceLang) : null;
  const progress = ((index) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-[#EAF7F4] font-kids text-slate-900">
      <div className={`mx-auto px-5 pt-6 transition-[max-width] duration-300 ${padOpen ? "max-w-6xl" : "max-w-2xl"}`}>
        <KikoCheer cheer={kiko} />
        {young && <StarCatch show={star} childId={childId} lang={voiceLang} />}
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => { stopVoice(); navigate(`/kid/${childId}/subject/${encodeURIComponent(subject)}`); }} className="rounded-full font-parent" data-testid="quiz-exit">
            <ArrowLeft size={18} />
          </Button>
          <div className="flex-1">
            <div className="flex justify-between text-sm font-parent text-slate-500 mb-1">
              <span data-testid="quiz-header-title">{title} · {bilingual && voiceLang === "fr" ? levelLabel(levelName, "fr") : levelName}</span>
              <span>{index + 1}/{questions.length}</span>
            </div>
            <div className="h-3 rounded-full bg-slate-200 overflow-hidden border-2 border-slate-900">
              <motion.div className="h-full bg-blue-600" animate={{ width: `${progress}%` }} transition={{ duration: 0.3 }} />
            </div>
            {questions.length > 0 && <KikoPath current={index + (revealed ? 1 : 0)} total={questions.length} />}
          </div>
        </div>

        {true && (
          <div className="flex items-center justify-between gap-2 mt-3 font-parent" data-testid="voice-controls">
            {isMaths ? <button onClick={() => setPadOpen((o) => !o)} data-testid="open-copybook-btn"
              className={`inline-flex items-center gap-2 rounded-full border-2 border-slate-900 px-4 h-9 text-sm font-bold shadow-[3px_3px_0_0_#0F172A] active:translate-x-0.5 active:translate-y-0.5 transition-transform ${padOpen ? "bg-slate-900 text-white" : "bg-blue-600 text-white"}`}>
              <PencilRuler size={16} /> {padOpen ? (voiceLang === "fr" ? "Fermer" : "Close") : (voiceLang === "fr" ? "Cahier" : "Copybook")}
            </button> : <span />}
            <div className="flex items-center gap-2">
              {bilingual && <div className="flex rounded-full border-2 border-slate-900 overflow-hidden bg-white">
                <button onClick={() => switchLang("en")} data-testid="voice-lang-en"
                  className={`px-3 py-1 text-xs font-bold ${voiceLang === "en" ? "bg-teal-500 text-white" : "text-slate-600"}`}>EN</button>
                <button onClick={() => switchLang("fr")} data-testid="voice-lang-fr"
                  className={`px-3 py-1 text-xs font-bold ${voiceLang === "fr" ? "bg-teal-500 text-white" : "text-slate-600"}`}>FR</button>
              </div>}
              <button onClick={cycleSpeed} data-testid="voice-speed-btn"
                className={`h-9 px-2.5 rounded-full border-2 border-slate-900 flex items-center gap-1 text-xs font-bold ${voiceSpeed === "normal" ? "bg-white text-slate-600" : "bg-emerald-200 text-emerald-900"}`}
                title={SPEED_LABEL[voiceLang][voiceSpeed]}>
                <Turtle size={16} />
                <span data-testid="voice-speed-label">{SPEED_SHORT[voiceLang][voiceSpeed]}</span>
              </button>
              <button onClick={toggleVoice} data-testid="voice-toggle"
                className={`w-9 h-9 rounded-full border-2 border-slate-900 flex items-center justify-center ${voiceOn ? "bg-yellow-300" : "bg-white"}`}
                title={voiceOn ? "Voice on" : "Voice off"}>
                {voiceOn ? <Volume2 size={16} /> : <VolumeX size={16} className="text-slate-400" />}
              </button>
            </div>
          </div>
        )}

        <div className={padOpen ? "md:grid md:grid-cols-[minmax(0,1fr)_320px] lg:grid-cols-[minmax(0,1fr)_440px] xl:grid-cols-[minmax(0,1fr)_500px] md:gap-4 md:items-start" : ""}>
          <div className="min-w-0 md:col-start-1 md:row-start-1">
            <AnimatePresence mode="wait">
              <motion.div key={index}
            initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="mt-4">
            <div className="nb-card bg-white p-4 sm:p-6 relative">
              {(() => {
                const geo = parseGeometry(q.question);
                if (geo) return <GeometryDiagram spec={geo} compact={padOpen} />;
                const story = subject === "Maths" ? parseStory(q.question) : null;
                if (story) return <StoryDiagram spec={story} lang={voiceLang} />;
                if (q.has_image) return (
                  <QuestionImage
                    key={`${index}-${voiceLang}`}
                    chapterId={chapter} level={level} qi={index} lang={voiceLang}
                    className="w-full mx-auto mb-3 rounded-2xl border-4 border-slate-900 object-contain bg-white"
                    style={{ boxShadow: "4px 4px 0 0 #0F172A", maxHeight: padOpen ? "18vh" : "24vh" }}
                  />
                );
                return null;
              })()}
              {young && !started && !result && <StartGate lang={voiceLang} onStart={() => { setStarted(true); if (voiceOn) readAloud(q); }} />}
              <h2 className={`${young ? "text-2xl sm:text-4xl leading-tight pr-16" : "text-xl sm:text-2xl leading-snug pr-10"} font-bold`} data-testid="quiz-question">
                <QuestionText text={q.question} activeWord={readProgress} lang={voiceLang} />
              </h2>
              <div className="flex flex-wrap gap-2 mt-3">
                {young && (
                  <button onClick={readAlong} data-testid="quiz-listen-again-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-teal-500 text-white border-4 border-slate-900 px-5 py-2.5 text-lg font-bold shadow-[3px_3px_0_0_#0F172A] active:translate-x-0.5 active:translate-y-0.5 transition-transform">
                    <Volume2 size={24} /> {voiceLang === "fr" ? "Écouter encore" : "Listen again"}
                  </button>
                )}
                <button onClick={readAlong} data-testid="quiz-readalong-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-violet-300 border-2 border-slate-900 px-3 py-1 text-sm font-bold text-slate-900 shadow-[2px_2px_0_0_#0F172A] active:translate-x-0.5 active:translate-y-0.5 transition-transform">
                  <BookOpenText size={15} /> {voiceLang === "fr" ? "Lire avec moi" : "Read along"}
                </button>
              </div>
              {!revealed && <KreolHint hint={q.kreol_hint} big={young} onUse={() => api.post(`/children/${childId}/hint-used`, { subject, chapter_id: chapter, level, qi: index, question: q.question }).catch(() => {})} />}
              <ReportQuestion key={`rep-${index}`} lang={voiceLang} reported={reportedQs.includes(index)}
                onReported={() => setReportedQs((r) => [...r, index])}
                payload={{ chapter_id: chapter, level: Number(level), qi: index, lang: voiceLang, question: q.question, source: "quiz", child_id: childId }} />
              {reportedQs.includes(index) && !revealed && (
                <button onClick={skipReported} data-testid="quiz-skip-reported"
                  className="mt-2 ml-3 inline-flex items-center gap-1 rounded-full bg-amber-300 border-2 border-slate-900 px-3 py-1 text-sm font-bold">
                  {voiceLang === "fr" ? "Passer cette question →" : "Skip this question →"}
                </button>
              )}
              {true && (
                <button onClick={() => readAloud(q)} data-testid="quiz-read-btn"
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-teal-500 border-2 border-slate-900 flex items-center justify-center shadow-[3px_3px_0_0_#0F172A] active:translate-x-0.5 active:translate-y-0.5 transition-transform"
                  title="Read question aloud">
                  <Volume2 size={18} color="#fff" />
                </button>
              )}
            </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {padOpen && (
            <div className="mt-6 md:mt-8 md:col-start-2 md:row-start-1 md:row-span-2 space-y-4" data-testid="copybook-column">
              <CalculationPad key={`${progKey}_q${index}`} open={padOpen} setOpen={() => { setPadOpen(false); setExtraPads([]); }} question={q?.question} storageKey={`${progKey}_q${index}`} lang={voiceLang} />
              {extraPads.map((id, i) => (
                <CalculationPad key={id} open={true} setOpen={() => setExtraPads((p) => p.filter((x) => x !== id))} question={null} label={`Extra working ${i + 2}`} lang={voiceLang} />
              ))}
              {extraPads.length < 2 && (
                <button onClick={() => setExtraPads((p) => [...p, Date.now()])} data-testid="add-calc-btn"
                  className="w-full rounded-xl border-2 border-dashed border-slate-300 text-slate-500 text-sm font-parent font-semibold py-2.5 hover:border-slate-400 hover:text-slate-700 transition-colors">
                  + Add another calculation
                </button>
              )}
            </div>
          )}

          <div className="min-w-0 md:col-start-1 md:row-start-2">
            <AnimatePresence mode="wait">
              <motion.div key={index}
                initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }}
                transition={{ type: "spring", stiffness: 300, damping: 25 }}>
            <div className="grid gap-2.5 mt-4">
              {q.options.map((opt, i) => {
                const isChosen = selected === i;
                const isAnswer = i === q.answer;
                let cls = "bg-white hover:bg-slate-50";
                let badgeCls = "bg-yellow-300 text-slate-900";
                if (revealed) {
                  if (isAnswer) { cls = "bg-green-500 text-white"; badgeCls = "bg-white text-green-600"; }
                  else if (isChosen) { cls = "bg-red-500 text-white"; badgeCls = "bg-white text-red-600"; }
                  else { cls = "bg-white text-slate-400 opacity-60"; }
                }
                return (
                  <motion.button
                    key={i}
                    whileTap={revealed ? {} : { scale: 0.98 }}
                    disabled={revealed}
                    onClick={() => choose(i)}
                    data-testid={`quiz-option-${i}`}
                    className={`flex items-center gap-3 rounded-2xl border-4 border-slate-900 text-left font-parent transition-all ${young ? "p-4 text-xl sm:text-2xl font-semibold" : "p-3 sm:p-3.5 text-base sm:text-lg"} ${cls} ${readingOpt === i && !revealed ? "ring-4 ring-teal-300" : ""}`}
                    style={{ boxShadow: "4px 4px 0 0 #0F172A" }}
                  >
                    <span className={`w-8 h-8 rounded-full border-2 border-slate-900 flex items-center justify-center font-bold shrink-0 ${badgeCls}`}>
                      {revealed && isAnswer ? <Check size={18} strokeWidth={3} />
                        : revealed && isChosen ? <X size={18} strokeWidth={3} />
                        : String.fromCharCode(65 + i)}
                    </span>
                    <OptionPic pics={q.option_pics} k={i} big={young} />
                    <span className="flex-1"><MathText>{optionLabel(opt, q.has_image, voiceLang)}</MathText></span>
                    {!revealed && (
                      <span role="button" tabIndex={0} data-testid={`quiz-option-say-${i}`}
                        onClick={(e) => { e.stopPropagation(); playVoice(answerSpeech(i, optionLabel(opt, q.has_image, voiceLang), voiceLang), voiceLang); }}
                        className={`${young ? "w-11 h-11" : "w-7 h-7"} rounded-full bg-teal-500 border-2 border-slate-900 flex items-center justify-center shrink-0 cursor-pointer`}
                        title="Hear this answer">
                        <Volume2 size={young ? 20 : 13} color="#fff" />
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>

            <AnimatePresence>
              {revealed && feedback && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  onClick={feedback.correct ? advanceNow : undefined}
                  data-testid="quiz-continue"
                  className={`mt-5 nb-card p-6 relative overflow-hidden ${feedback.correct ? "bg-green-50 cursor-pointer" : "bg-amber-50"}`}
                >
                  {/* visual burst */}
                  {feedback.correct && (
                    <div className="absolute inset-0 pointer-events-none">
                      {[...Array(6)].map((_, k) => (
                        <motion.div key={k}
                          initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
                          animate={{ opacity: [0, 1, 0], scale: [0, 1, 0.8], x: (k - 3) * 40, y: [0, -30 - k * 6] }}
                          transition={{ duration: 1.1, delay: 0.05 * k }}
                          className="absolute left-1/2 top-6">
                          <Star size={16} fill="#FACC15" className="text-yellow-400" />
                        </motion.div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 relative">
                    <motion.div
                      initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 300, damping: 12 }}
                      className={`w-14 h-14 rounded-full border-4 border-slate-900 flex items-center justify-center shrink-0 ${feedback.correct ? "bg-green-400" : "bg-amber-300"}`}
                      style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
                      {feedback.correct ? <Sparkles color="#fff" size={26} /> : <Heart color="#fff" size={26} fill="#fff" />}
                    </motion.div>
                    <div>
                      <p className="text-2xl font-bold" style={{ color: feedback.correct ? "#059669" : "#B45309" }} data-testid="quiz-feedback">
                        {feedback.message}
                      </p>
                      {!feedback.correct && (
                        <p className="font-parent text-sm text-slate-600 mt-0.5 flex items-center gap-1">
                          <CheckCircle2 size={14} className="text-green-600" />
                          {voiceLang === "fr" ? "Bonne réponse :" : "Correct answer:"}{" "}
                          <span className="font-semibold text-green-700"><MathText>{optionLabel(q.options[q.answer], q.has_image, voiceLang)}</MathText></span>
                        </p>
                      )}
                    </div>
                  </div>
                  {feedback.correct && feedback.usedCopybook && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.15 }}
                      data-testid="quiz-effort-praise"
                      className="mt-4 flex items-center gap-2 rounded-xl bg-pink-100 border-2 border-slate-900 p-3">
                      <div className="w-8 h-8 rounded-lg bg-pink-500 flex items-center justify-center shrink-0">
                        <Heart color="#fff" size={16} fill="#fff" />
                      </div>
                      <p className="font-parent text-sm font-semibold text-pink-700">
                        {voiceLang === "fr"
                          ? "Bravo, tu as utilisé ton cahier ! +5 points d'effort ⭐"
                          : "Brilliant — you used your copybook! +5 effort points ⭐"}
                      </p>
                    </motion.div>
                  )}
                  {q.explanation && (
                    <div className="mt-4 flex items-start gap-2 rounded-xl bg-white/70 border-2 border-slate-900 p-3" data-testid="quiz-explanation">
                      <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
                        <Lightbulb color="#fff" size={16} />
                      </div>
                      <p className="font-parent text-sm text-slate-700 leading-relaxed"><MathText>{q.explanation}</MathText></p>
                    </div>
                  )}
                  {!feedback.correct && working && (
                    <div className="mt-4 rounded-xl bg-white border-2 border-slate-900 p-4" data-testid="quiz-working">
                      <p className="font-parent font-bold text-slate-800 mb-2 flex items-center gap-2">
                        <Lightbulb size={16} className="text-blue-500" />
                        {voiceLang === "fr" ? "Comment faire le calcul" : "How to work it out"}
                      </p>
                      <ol className="space-y-1.5">
                        {working.map((step, si) => (
                          <li key={si} className="flex items-start gap-2 font-parent text-sm text-slate-700" data-testid={`working-step-${si}`}>
                            <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${si === working.length - 1 ? "bg-green-500 text-white" : "bg-blue-100 text-blue-700"}`}>
                              {si === working.length - 1 ? "✓" : si + 1}
                            </span>
                            <span className={si === working.length - 1 ? "font-semibold text-green-700" : ""}><MathText>{step}</MathText></span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {feedback.correct ? (
                    <p className="text-center font-parent text-xs text-slate-400 mt-4">
                      {submitting ? (voiceLang === "fr" ? "Un instant…" : "One moment…")
                        : (voiceLang === "fr" ? "Touche pour continuer →" : "Tap to continue →")}
                    </p>
                  ) : (
                    <div className="flex justify-center mt-5">
                      <Button onClick={advanceNow} data-testid="quiz-next-btn"
                        className="rounded-full bg-blue-600 hover:bg-blue-700 h-12 px-8 border-2 border-slate-900 shadow-[4px_4px_0_0_#0F172A] font-parent font-semibold text-base">
                        {submitting ? (voiceLang === "fr" ? "Un instant…" : "One moment…")
                          : (voiceLang === "fr" ? "Suivant →" : "Next →")}
                      </Button>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}

function Confetti() {
  const colors = ["#FACC15", "#10B981", "#3B82F6", "#EC4899", "#F59E0B", "#8B5CF6"];
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" aria-hidden>
      {[...Array(40)].map((_, k) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const dur = 1.8 + Math.random() * 1.2;
        const size = 8 + Math.random() * 8;
        const color = colors[k % colors.length];
        return (
          <motion.div key={k}
            initial={{ y: -40, opacity: 0, rotate: 0 }}
            animate={{ y: "110vh", opacity: [0, 1, 1, 0.8], rotate: 360 + Math.random() * 360 }}
            transition={{ duration: dur, delay, ease: "easeIn" }}
            style={{ position: "absolute", left: `${left}%`, width: size, height: size * 1.4,
              background: color, borderRadius: 2 }}
          />
        );
      })}
    </div>
  );
}

function ResultScreen({ result, subject, title, voiceLang = "en", showUnlock = false, onUpgrade, onRetry, onSubject, onCollection, onHome, starChildId = null }) {
  const [levelStar, setLevelStar] = useState(0);
  useEffect(() => {
    if (!starChildId) return undefined;
    const t = setTimeout(() => setLevelStar(Date.now()), 2500);
    return () => clearTimeout(t);
  }, [starChildId]);
  const { score, total, percent, stars_earned, passed, praise, new_shield, review,
          effort_gained = 0, retry_win = false, avatar_unlock_available = false, weekly_completed = false } = result;
  const pct = typeof percent === "number" ? percent : Math.round((score / (total || 1)) * 100);
  const fr = voiceLang === "fr";
  const encourage = fr ? "Allez, tu peux y arriver !" : "Come on, you can make it!";
  const [chapterDone, setChapterDone] = useState(Boolean(new_shield));
  useEffect(() => {
    if (new_shield) return;
    if (passed) playLevelCheer(); else playEncourage();
    if (localStorage.getItem("aa_voice_on") !== "off") {
      const t = setTimeout(() => playVoice(passed ? (fr ? "Bravo ! Niveau réussi !" : "Hooray! Level complete!") : encourage, voiceLang), 700);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="min-h-screen bg-[#EAF7F4] font-kids text-slate-900 py-8">
      {passed && <Confetti />}
      {starChildId && <StarCatch show={levelStar} childId={starChildId} lang={voiceLang} />}
      {chapterDone && <ChapterCelebration name={result.child?.name || ""} childId={result.child?.id} chapter={title} lang={voiceLang} onClose={() => setChapterDone(false)} />}
      {!chapterDone && <RewardCelebration shield={false} avatar={Boolean(avatar_unlock_available || weekly_completed)} lang={voiceLang} onCollection={onCollection} />}
      <div className="max-w-2xl mx-auto px-5">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 18 }}
          className="nb-card bg-white p-8 text-center" data-testid="quiz-result">
          <motion.p initial={{ scale: 0 }} animate={{ scale: [0, 1.2, 1] }} transition={{ duration: 0.5 }}
            className="text-3xl font-bold" style={{ color: passed ? "#10B981" : "#F59E0B" }}>
            {passed ? praise : encourage}
          </motion.p>
          {!passed && (
            <p className="font-parent text-lg text-slate-600 mt-2" data-testid="result-encourage">
              {fr ? "Chaque essai te rend plus fort. Recommence, tu y es presque !" : "Every try makes you stronger. Have another go — you're nearly there!"}
            </p>
          )}
          <div className="flex justify-center gap-2 mt-5">
            {[1, 2, 3].map((s) => (
              <motion.div key={s} initial={{ scale: 0, rotate: -30 }} animate={{ scale: 1, rotate: 0 }} transition={{ delay: 0.2 + s * 0.15, type: "spring", stiffness: 300, damping: 12 }}>
                <Star size={48} fill={s <= stars_earned ? "#FACC15" : "#E2E8F0"} className={s <= stars_earned ? "text-yellow-400" : "text-slate-200"} />
              </motion.div>
            ))}
          </div>
          <p className="text-xl font-parent mt-5">{fr ? "Ton score" : "You scored"} <span className="font-bold">{score}/{total}</span></p>
          <motion.div
            initial={{ scale: 0 }} animate={{ scale: [0, 1.15, 1] }} transition={{ delay: 0.4, duration: 0.5 }}
            className="mx-auto mt-3 inline-flex items-center justify-center w-24 h-24 rounded-full border-4 border-slate-900"
            style={{ background: passed ? "#10B981" : "#F59E0B", boxShadow: "4px 4px 0 0 #0F172A" }}
            data-testid="result-percent">
            <span className="text-3xl font-bold text-white">{pct}%</span>
          </motion.div>
          <p className="font-parent font-semibold mt-3" style={{ color: passed ? "#059669" : "#B45309" }}>
            {passed ? (fr ? "Niveau réussi ! 🎉" : "Level complete!") : (fr ? "Presque ! Réessaie 💪" : "Almost there — try again!")}
          </p>
          {new_shield && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
              className="inline-flex items-center gap-2 mt-4 bg-blue-50 border-2 border-blue-600 rounded-full px-4 py-2 font-parent font-semibold text-blue-700">
              <Shield size={18} /> {fr ? "Nouveau bouclier débloqué !" : "New shield unlocked!"}
            </motion.div>
          )}
          {retry_win && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }}
              data-testid="result-retry-praise"
              className="mt-4 mx-auto max-w-md bg-amber-50 border-2 border-amber-500 rounded-2xl px-4 py-3 font-parent font-semibold text-amber-700">
              {fr ? "Tu n'as pas abandonné et tu as réussi. Quel courage ! 💪" : "You didn't give up and you got there. That's real grit! 💪"}
            </motion.div>
          )}
          {effort_gained > 0 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
              data-testid="result-effort"
              className="inline-flex items-center gap-2 mt-4 bg-pink-50 border-2 border-pink-500 rounded-full px-4 py-2 font-parent font-semibold text-pink-700">
              <Heart size={18} fill="currentColor" /> +{effort_gained} {fr ? "points d'effort" : "effort points"}
            </motion.div>
          )}
          {weekly_completed && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95 }}
              data-testid="result-weekly-complete"
              className="mt-4 mx-auto max-w-md bg-green-100 border-2 border-green-600 rounded-2xl px-4 py-3 font-parent font-semibold text-green-700 flex items-center justify-center gap-2">
              <Target size={18} /> {fr ? "Défi de la semaine réussi ! Avatar bonus débloqué 🎁" : "Weekly challenge complete! Bonus avatar unlocked 🎁"}
            </motion.div>
          )}
          {avatar_unlock_available && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.0 }}
              data-testid="result-avatar-unlock"
              className="mt-5 mx-auto max-w-md bg-amber-300 border-4 border-slate-900 rounded-2xl p-4" style={{ boxShadow: "4px 4px 0 0 #0F172A" }}>
              <p className="font-parent font-bold text-slate-900 flex items-center justify-center gap-2">
                <Gift size={20} /> {fr ? "Tu as gagné un nouvel avatar !" : "You've earned a new avatar!"}
              </p>
              <Button onClick={onCollection} data-testid="result-collection-btn"
                className="mt-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white h-11 px-6 border-2 border-slate-900 font-parent font-semibold">
                {fr ? "Choisir mon avatar" : "Pick my avatar"} <ArrowRight size={16} className="ml-1" />
              </Button>
            </motion.div>
          )}
          {!passed && (
            <p className="font-parent text-sm text-slate-500 mt-3">
              {fr ? "Obtiens 60 % ou plus pour terminer ce niveau et débloquer le suivant." : "Score 60% or more to complete this level and unlock the next."}
            </p>
          )}
          <div className="flex flex-wrap gap-3 justify-center mt-7 font-parent">
            <Button onClick={onRetry} variant="outline" className="rounded-full border-2 border-slate-900 h-11 px-5" data-testid="result-retry">
              <RotateCcw size={16} className="mr-1.5" /> {fr ? "Réessayer" : "Try again"}
            </Button>
            <Button onClick={onSubject} className="rounded-full bg-blue-600 hover:bg-blue-700 h-11 px-5 border-2 border-slate-900 shadow-[4px_4px_0_0_#0F172A]" data-testid="result-continue">
              <Trophy size={16} className="mr-1.5" /> {fr ? "Continuer" : "Keep going"}
            </Button>
            <Button onClick={onHome} variant="ghost" className="rounded-full h-11 px-5" data-testid="result-home">
              <Home size={16} className="mr-1.5" /> {fr ? "Accueil" : "Home"}
            </Button>
          </div>
        </motion.div>

        {/* Parent unlock trigger — shown after a free/demo level */}
        {showUnlock && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="nb-card bg-blue-600 text-white p-6 sm:p-7 mt-6 relative overflow-hidden"
            data-testid="result-unlock"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-yellow-300 border-4 border-slate-900 flex items-center justify-center shrink-0" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
                <Crown className="text-slate-900" size={26} />
              </div>
              <div className="flex-1">
                <h3 className="font-kids text-xl sm:text-2xl font-bold">
                  {fr ? "Bravo, la démo est terminée !" : "Nice — that was the free demo!"}
                </h3>
                <p className="font-parent text-blue-100 text-sm mt-1">
                  {fr
                    ? "Parents : débloquez tous les chapitres, les 5 matières et plusieurs profils d'enfants pour continuer l'aventure."
                    : "Parents: unlock all chapters, all subjects and more child profiles to keep the adventure going."}
                </p>
              </div>
            </div>
            <Button onClick={onUpgrade}
              className="w-full sm:w-auto mt-5 h-12 px-7 rounded-full bg-yellow-300 text-slate-900 hover:bg-yellow-400 border-2 border-slate-900 shadow-[4px_4px_0_0_#0F172A] font-parent font-semibold"
              data-testid="result-unlock-btn">
              <Sparkles size={18} className="mr-1.5" /> {fr ? "Voir les offres" : "See plans"} <ArrowRight size={18} className="ml-1" />
            </Button>
          </motion.div>
        )}

        <div className="mt-6 space-y-3 pb-10">
          <h3 className="text-lg font-bold px-1">{fr ? "Révisons ensemble" : "Let's review"}</h3>
          {review.map((r, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="nb-card bg-white p-5" data-testid={`review-${i}`}>
              <div className="flex items-start gap-2">
                <span className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${r.is_correct ? "bg-green-500" : "bg-red-500"}`}>
                  {r.is_correct ? <Check color="#fff" size={16} strokeWidth={3} /> : <X color="#fff" size={16} strokeWidth={3} />}
                </span>
                <p className="font-parent font-semibold"><MathText>{r.question}</MathText></p>
              </div>
              <div className="mt-3 pl-8 space-y-1 font-parent text-sm">
                {!r.is_correct && r.your_answer >= 0 && (
                  <p className="text-red-600">{fr ? "Ta réponse :" : "Your answer:"} <MathText>{r.options[r.your_answer]}</MathText></p>
                )}
                <p className="text-green-700">{fr ? "Bonne réponse :" : "Correct:"} <MathText>{r.options[r.correct_answer]}</MathText></p>
                {r.explanation && (
                  <p className="text-slate-500 flex items-start gap-1 mt-1">
                    <Lightbulb size={14} className="text-blue-500 shrink-0 mt-0.5" /> <span className="italic"><MathText>{r.explanation}</MathText></span>
                  </p>
                )}
                {!r.is_correct && (() => {
                  const rw = buildWorking(r.question, voiceLang);
                  return rw ? (
                    <div className="mt-2 rounded-xl bg-slate-50 border-2 border-slate-200 p-3" data-testid={`review-working-${i}`}>
                      <p className="font-semibold text-slate-700 mb-1">{fr ? "Comment faire :" : "How to work it out:"}</p>
                      <ol className="space-y-1">
                        {rw.map((step, si) => (
                          <li key={si} className="flex items-start gap-2 text-slate-600">
                            <span className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${si === rw.length - 1 ? "bg-green-500 text-white" : "bg-blue-100 text-blue-700"}`}>{si === rw.length - 1 ? "✓" : si + 1}</span>
                            <span className={si === rw.length - 1 ? "font-semibold text-green-700" : ""}><MathText>{step}</MathText></span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ) : null;
                })()}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
